import { useRef, useCallback, useEffect, useState } from "react";
import { useFrame } from "@react-three/fiber";
import type { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";

import type { PropId } from "@/types/engine.types";
import { useEngineStore } from "@/store/useEngineStore";

import type { Vector3Tuple } from "three";

interface InteractivePropProps {
  propId: PropId;
  label: string;
  clickMessage: string;
  position: Vector3Tuple;
  children: React.ReactNode;
}

export function InteractiveProp({
  propId,
  label,
  clickMessage,
  position,
  children,
}: InteractivePropProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const hoveredRef = useRef(false);
  const clickTimeRef = useRef(0);
  const baseY = position[1];
  const activateProp = useEngineStore((s) => s.activateProp);

  const onPointerEnter = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      hoveredRef.current = true;
      window.dispatchEvent(
        new CustomEvent("prop-hover", {
          detail: { label, x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
        }),
      );
    },
    [label],
  );

  const onPointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (hoveredRef.current) {
        window.dispatchEvent(
          new CustomEvent("prop-hover-move", {
            detail: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
          }),
        );
      }
    },
    [],
  );

  const onPointerLeave = useCallback(() => {
    hoveredRef.current = false;
    window.dispatchEvent(new CustomEvent("prop-hover-end"));
  }, []);

  const onClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      activateProp({ propId });
      clickTimeRef.current = performance.now();
      window.dispatchEvent(
        new CustomEvent("prop-click", {
          detail: {
            message: clickMessage,
            x: e.nativeEvent.clientX,
            y: e.nativeEvent.clientY,
          },
        }),
      );
      // Walk the agent toward this prop
      window.dispatchEvent(
        new CustomEvent("prop-walk", {
          detail: { position, propId },
        }),
      );
    },
    [propId, clickMessage, activateProp, position],
  );

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.elapsedTime;
    let y = baseY;

    if (hoveredRef.current) {
      y = baseY + Math.sin(t * 3) * 0.06 + 0.06;
    } else {
      y = groupRef.current.position.y + (baseY - groupRef.current.position.y) * 0.1;
    }

    const elapsed = performance.now() - clickTimeRef.current;
    if (elapsed < 500) {
      y += Math.sin((elapsed / 500) * Math.PI) * 0.2;
    }

    groupRef.current.position.y = y;
  });

  return (
    <group
      ref={groupRef}
      position={[position[0], position[1], position[2]]}
      onPointerEnter={onPointerEnter}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
    >
      {/* Invisible hitbox for reliable click detection */}
      <mesh position={[0, 1, 0]}>
        <boxGeometry args={[2, 2.5, 2]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {children}
    </group>
  );
}

/** HTML tooltip overlay — render outside Canvas at route level */
export function PropTooltip() {
  const [state, setState] = useState<{
    visible: boolean;
    label: string;
    x: number;
    y: number;
  }>({ visible: false, label: "", x: 0, y: 0 });

  useEffect(() => {
    function onHover(e: Event) {
      const { label, x, y } = (e as CustomEvent).detail;
      setState({ visible: true, label, x, y });
    }
    function onMove(e: Event) {
      const { x, y } = (e as CustomEvent).detail;
      setState((s) => ({ ...s, x, y }));
    }
    function onEnd() {
      setState((s) => ({ ...s, visible: false }));
    }
    window.addEventListener("prop-hover", onHover);
    window.addEventListener("prop-hover-move", onMove);
    window.addEventListener("prop-hover-end", onEnd);
    return () => {
      window.removeEventListener("prop-hover", onHover);
      window.removeEventListener("prop-hover-move", onMove);
      window.removeEventListener("prop-hover-end", onEnd);
    };
  }, []);

  if (!state.visible) return null;

  return (
    <div
      className="pointer-events-none fixed z-50 rounded-lg bg-[#2D2D3C]/90 px-3.5 py-1.5 text-[13px] font-medium tracking-wide text-white backdrop-blur transition-opacity"
      style={{ left: state.x + 15, top: state.y - 10 }}
    >
      {state.label}
    </div>
  );
}

/** HTML click feedback toast — render outside Canvas at route level */
export function PropClickFeedback() {
  const [state, setState] = useState<{
    visible: boolean;
    message: string;
    x: number;
    y: number;
  }>({ visible: false, message: "", x: 0, y: 0 });

  useEffect(() => {
    function onPropClick(e: Event) {
      const { message, x, y } = (e as CustomEvent).detail;
      setState({ visible: true, message, x, y });
      setTimeout(() => setState((s) => ({ ...s, visible: false })), 1200);
    }
    window.addEventListener("prop-click", onPropClick);
    return () => window.removeEventListener("prop-click", onPropClick);
  }, []);

  if (!state.visible) return null;

  return (
    <div
      className="pointer-events-none fixed z-50 rounded-xl bg-[#6ECFCF]/92 px-4 py-2 text-sm font-semibold tracking-wide text-white transition-opacity"
      style={{ left: state.x, top: state.y - 40 }}
    >
      {state.message}
    </div>
  );
}
